import React, { useState, useMemo } from 'react';

import { Badge } from '../../components/ui/badge';

import { CheckCircle2, XCircle, AlertCircle, RefreshCw, Loader2 } from 'lucide-react';

import { computeComplianceStatus } from './ccmiConstants';

export function ComplianceStatusPanel({ draft }) {
  const [expanded, setExpanded] = useState(true);
  const status = useMemo(() => computeComplianceStatus(draft), [draft]);
  const pct = status.total > 0 ? Math.round((status.score / status.total) * 100) : 0;

  if (status.ok) {
    return (
      <div
        className="bg-emerald-50 border border-emerald-300 rounded-lg p-3"
        data-testid="ccmi-compliance-status-panel"
        data-state="complete"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-700" />
            <div>
              <div className="font-semibold text-emerald-900" data-testid="ccmi-compliance-status-label">
                Projet COMPLET — tous les éléments obligatoires sont renseignés ({status.score}/{status.total})
              </div>
              <div className="text-xs text-emerald-700 mt-0.5">
                Vous pouvez générer le projet de mise en conformité.
              </div>
            </div>
          </div>
          <Badge className="bg-emerald-700 text-white font-semibold" data-testid="ccmi-compliance-status-badge">COMPLET</Badge>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2"
      data-testid="ccmi-compliance-status-panel"
      data-state="incomplete"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-amber-700" />
          <div>
            <div className="font-semibold text-amber-900" data-testid="ccmi-compliance-status-label">
              Projet INCOMPLET — {status.missing.length} élément{status.missing.length > 1 ? 's' : ''} manquant{status.missing.length > 1 ? 's' : ''}
            </div>
            <div className="text-xs text-amber-800 mt-0.5">
              Progression : <span className="font-mono font-semibold">{status.score} / {status.total}</span> champs renseignés ({pct}%)
            </div>
          </div>
        </div>
        <Badge className="bg-amber-700 text-white font-semibold" data-testid="ccmi-compliance-status-badge">INCOMPLET</Badge>
      </div>

      <div className="w-full bg-amber-100 rounded-full h-1.5 overflow-hidden" aria-hidden="true">
        <div
          className="bg-amber-600 h-full transition-all"
          style={{ width: `${pct}%` }}
          data-testid="ccmi-compliance-progress-bar"
        />
      </div>

      <div className="text-xs">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-amber-900 underline hover:text-amber-700"
          data-testid="ccmi-compliance-toggle-missing"
        >
          {expanded ? 'Masquer le détail' : 'Voir les éléments manquants'}
        </button>
        {expanded && (
          <ul
            className="list-disc ml-5 mt-1.5 space-y-0.5 text-amber-900"
            data-testid="ccmi-compliance-missing-list"
          >
            {status.missing.map((item) => (
              <li key={item.key} data-testid={`ccmi-compliance-missing-${item.key}`}>{item.label}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * V38.18 — Indicateur discret d'autosave silencieux.
 *
 * États affichés :
 *  - 'idle'    : rien à afficher
 *  - 'pending' : "En attente de sauvegarde…" (debounce en cours)
 *  - 'saving'  : "Enregistrement…" + spinner
 *  - 'saved'   : "Enregistré il y a Xs" (s'auto-rafraîchit toutes les 1s)
 *  - 'error'   : "Échec autosave : ..."
 *
 * Design : bandeau slate très discret, à l'intérieur du modal, type Notion.
 */
export function AutosaveIndicator({ status, savedAt, error, tick }) {
  // `tick` force le re-render pour rafraîchir le compteur "il y a Xs"
  const ago = useMemo(() => {
    if (!savedAt) return null;
    const ms = Date.now() - new Date(savedAt).getTime();
    if (!isFinite(ms) || ms < 0) return null;
    const s = Math.floor(ms / 1000);
    if (s < 5) return 'à l\u2019instant';
    if (s < 60) return `il y a ${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `il y a ${m} min`;
    const h = Math.floor(m / 60);
    return `il y a ${h} h`;
  }, [savedAt, tick]);

  if (status === 'idle' && !savedAt) return null;

  let icon = null;
  let label = null;
  let cls = 'bg-slate-50 border-slate-200 text-slate-600';
  if (status === 'saving') {
    icon = <Loader2 className="h-3.5 w-3.5 animate-spin" />;
    label = 'Enregistrement…';
    cls = 'bg-blue-50 border-blue-200 text-blue-700';
  } else if (status === 'pending') {
    icon = <RefreshCw className="h-3.5 w-3.5" />;
    label = 'En attente de sauvegarde…';
    cls = 'bg-slate-50 border-slate-200 text-slate-500';
  } else if (status === 'error') {
    icon = <XCircle className="h-3.5 w-3.5" />;
    label = `Échec autosave : ${error || 'erreur inconnue'}`;
    cls = 'bg-red-50 border-red-200 text-red-700';
  } else if (status === 'saved' || savedAt) {
    icon = <CheckCircle2 className="h-3.5 w-3.5" />;
    label = ago ? `Enregistré ${ago}` : 'Enregistré';
    cls = 'bg-emerald-50 border-emerald-200 text-emerald-700';
  }

  return (
    <div
      className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border ${cls}`}
      data-testid="ccmi-compli-autosave-indicator"
      data-status={status}
      role="status"
      aria-live="polite"
    >
      {icon}
      <span data-testid="ccmi-compli-autosave-label">{label}</span>
    </div>
  );
}
